// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import type { RouterHistory } from 'react-router';
import PostSearchInput from 'components/PostSearchInput';
import {
  filterObject,
  mapObject,
  updateObject,
  isEmpty,
  values
} from 'utils/functional';
import { truncateToUTF8Bytes } from 'utils/string';
import {
  resetSearchFilters,
  setSearchQuery
} from '../redux/main/actions/postSearch';

type OwnProps = {
  history: RouterHistory,
  onSearch?: Object => void,
  inPageHeader?: boolean,
  isStatic?: boolean,
  pill?: string
};

type Props = {
  ...$Exact<OwnProps>,
  homePageUrl: string,
  query: string,
  resetSearchFilters: typeof resetSearchFilters,
  setSearchQuery: typeof setSearchQuery
};

type State = {
  query: string,
  prevProps: {
    query: string
  }
};

const toQueryParamKey = (k: string) => {
  const mapping = { query: 'pq' };
  return mapping[k] || k;
};

const makeQueryString = (props: { type?: string, query?: string }) => {
  const search = values(
    mapObject(filterObject(props, k => !isEmpty(k)), (k, v) => [
      k,
      `${toQueryParamKey(k)}=${v}`
    ])
  ).join('&');
  return isEmpty(search) ? '' : `?${search}`;
};

class PostSearchInputContainer extends React.Component<Props, State> {
  state = {
    query: this.props.query || '',
    // eslint-disable-next-line react/no-unused-state
    prevProps: {
      query: this.props.query
    }
  };

  onSearch = (props: { homePageUrl: string, query?: string }) => {
    const { homePageUrl, ...queryProps } = props;
    const queryString = makeQueryString(
      updateObject(queryProps, {
        searchMode: 'true'
      })
    );
    this.props.history.push(`/${homePageUrl}${queryString}`);
  };

  static getDerivedStateFromProps(props: Props, state: State) {
    const updatedState = ['query'].reduce((acc, prop) => {
      if (props[prop] !== state.prevProps[prop]) {
        acc[prop] = props[prop];
      }
      return acc;
    }, {});

    if (Object.keys(updatedState).length > 0) {
      updatedState.prevProps = {
        query: props.query
      };
      return updatedState;
    }
    return null;
  }

  setQuery = (query: string, callback?: Function) => {
    // Algolia has a max query length of 512 bytes
    this.setState({ query: truncateToUTF8Bytes(query, 512) }, callback);
  };

  search = () => {
    this.props.resetSearchFilters();
    if (this.props.query !== this.state.query) {
      this.props.setSearchQuery(this.state.query, true);
    }
    const { query } = this.state;
    this.onSearch({
      query,
      homePageUrl: this.props.homePageUrl
    });
  };

  render() {
    const { query } = this.state;
    const { setQuery, search } = this;
    return (
      <PostSearchInput
        data-qa-id="post-search-input"
        query={query}
        search={search}
        placeholder={getText('Search articles')}
        setQuery={setQuery}
        clearQuery={() => {
          setQuery('', search);
        }}
        isStatic={this.props.isStatic}
        pill={this.props.pill}
      />
    );
  }
}

const mapStateToProps = ({ postSearch: { query } }) => ({ query });

export default withRouter(
  connect<Props, OwnProps, _, _, _, _>(
    mapStateToProps,
    // $FlowFixMe: react-redux complains about this type when wrapped in withRouter
    {
      resetSearchFilters,
      setSearchQuery
    }
  )(PostSearchInputContainer)
);
